"use client";
import React, { useState, useEffect } from 'react';
import {
    Drawer,
    Modal,
    Box,
    Typography,
    IconButton,
    CircularProgress,
    Grid2 as Grid,
    useMediaQuery
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DealCard from './DealCard';
import { useConfig } from '../ConfigProvider';
import { usePostHog } from 'posthog-js/react';

const VariantsDrawer = ({ 
    open, 
    onClose, 
    variantIds = [], 
    mainDeal,
    user,
    categories,
    userType,
    setOpenModal,
    setSelectedDeal,
    showCheckBox,
    onSelect,
    isDealSelected
}) => {
    
    const [variants, setVariants] = useState([]);
    const [loading, setLoading] = useState(false);
    const isMobile = useMediaQuery('(max-width:1023px)');
    const config = useConfig();
    const posthog = usePostHog();

    useEffect(() => {
        if (open && variantIds.length > 0) {
            fetchVariants();
        }
    }, [open, variantIds]);

    const fetchVariants = async () => {
        if (!config) return;
        
        setLoading(true);
        try {
            const url = `${config.siteApi.apiUrl}/crm/deals`;
            const postHogId = posthog.get_distinct_id();
            const queryParams = new URLSearchParams();
            
            // Add each productId as a separate parameter to create an array
            variantIds.forEach(id => {
                queryParams.append('productIds', id);
            });
            
            const response = await fetch(`${url}?${queryParams}`, {
                method: "GET",
                headers: {
                    'x-poln-user-id': postHogId
                },
            });
            
            if (!response.ok) {
                throw new Error('Failed to fetch variants');
            }
            
            const data = await response.json();
            setVariants(data.data || []);
        } catch (error) {
            setVariants([]);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        onClose();
        // Reset state on close
        setVariants([]);
        setLoading(false);
    };

    // Handle variant selection and close modal for creator userType
    const handleVariantSelect = (dealId) => {
        if (onSelect) {
            // Find the variant deal object to pass to the parent
            const variantDeal = variants.find(v => v.productId === dealId);
            if (variantDeal) {
                onSelect(dealId, variantDeal);
            } else {
                onSelect(dealId);
            }
        }
        // For creator userType, close the modal after selection with a small delay
        // to prevent any state conflicts
        if (userType === 'creator') {
            setTimeout(() => {
                handleClose();
            }, 100);
        }
    };

    // Content component that will be used in both mobile drawer and desktop modal
    const content = (
        <Box sx={{ 
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#f8f9fa'
        }}>
            {/* Header */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 2,
                backgroundColor: 'white',
                borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
            }}>
                <Typography variant="h6" fontWeight={600} color="black">
                    Product Variants ({variantIds.length})
                </Typography>
                <IconButton 
                    onClick={handleClose}
                    sx={{
                        backgroundColor: 'rgba(0, 0, 0, 0.05)',
                        '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.1)',
                        }
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>

            {/* Content */}
            <Box sx={{ 
                flex: 1,
                overflow: 'auto',
                p: 2
            }}>
                {loading ? (
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '50%',
                        minHeight: '200px'
                    }}>
                        <CircularProgress size={40} sx={{ color: 'var(--theme-dark)' }} />
                    </Box>
                ) : (
                    <>
                        {variants.length > 0 ? (
                            <Grid container spacing={2}>
                                {variants.map((variant) => (
                                    <DealCard
                                        key={variant.productId}
                                        deal={variant}
                                        user={user}
                                        categories={categories}
                                        userType={userType}
                                        showCheckBox={showCheckBox}
                                        showVariant={false}
                                        setOpenModal={setOpenModal}
                                        setSelectedDeal={setSelectedDeal}
                                        onSelect={handleVariantSelect}
                                        isDealSelected={isDealSelected}
                                    />
                                ))}
                            </Grid>
                        ) : (
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '50%',
                                minHeight: '200px'
                            }}>
                                <Typography variant="body1" color="text.secondary">
                                    No variants found
                                </Typography>
                            </Box>
                        )}
                    </>
                )}
            </Box>
        </Box>
    );

    // Mobile: use Drawer from bottom
    if (isMobile) {
        return (
            <Drawer
                anchor="bottom"
                open={open}
                onClose={handleClose}
                PaperProps={{
                    sx: {
                        width: '100%',
                        height: '90%',
                        borderRadius: '16px 16px 0 0',
                        overflow: 'hidden'
                    }
                }}
                ModalProps={{
                    keepMounted: false,
                }}
            >
                {content}
            </Drawer>
        );
    }

    // Desktop: use Modal in center
    return (
        <Modal
            open={open}
            onClose={handleClose}
            aria-labelledby="variants-modal-title"
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2
            }}
        >
            <Box sx={{
                width: '98%',
                maxWidth: '1600px',
                height: '90%',
                maxHeight: '900px',
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                outline: 'none'
            }}>
                {content}
            </Box>
        </Modal>
    );
};

export default VariantsDrawer; 